import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Text } from "@nbs/ui";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsHeatmap } from "@/features/charts/NbsHeatmap";

export type CoverageCell = {
  indicateurId: string;
  indicateurLibelle: string;
  territoryId: string;
  territoryLabel: string;
  periodYear: string;
  status: string;
  mesureCount: number;
};

export type CoverageMatrixProps = {
  readonly cells: readonly CoverageCell[];
  readonly completenessRatio?: number;
  readonly isLoading?: boolean;
  readonly onResetFilters?: () => void;
  /** Année proposée au chargement (ex. année snapshot du dashboard). */
  readonly preferredYear?: string | null;
};

const ALL_YEARS = "all";

function uniqueYears(cells: readonly CoverageCell[]): string[] {
  return [
    ...new Set(
      cells.map((c) => String(c.periodYear ?? "").trim()).filter((y) => /^\d{4}$/.test(y)),
    ),
  ].sort((a, b) => Number(a) - Number(b));
}

export function CoverageMatrix({
  cells,
  completenessRatio,
  isLoading,
  onResetFilters,
  preferredYear,
}: CoverageMatrixProps) {
  const years = useMemo(() => uniqueYears(cells), [cells]);
  const latestYear = years[years.length - 1] ?? null;

  // null = pas encore choisi par l’utilisateur → on suit preferred/latest.
  const [userYear, setUserYear] = useState<string | null>(null);

  const defaultYear =
    preferredYear && years.includes(preferredYear) ? preferredYear : (latestYear ?? ALL_YEARS);

  const activeYear =
    userYear === null
      ? defaultYear
      : userYear === ALL_YEARS || years.includes(userYear)
        ? userYear
        : defaultYear;

  const filteredCells = useMemo(
    () =>
      activeYear === ALL_YEARS ? cells : cells.filter((c) => String(c.periodYear) === activeYear),
    [cells, activeYear],
  );

  const heatmapCells = filteredCells.slice(0, 120).map((c) => ({
    row: c.indicateurLibelle,
    col: c.territoryLabel,
    ...(activeYear === ALL_YEARS ? { colMeta: String(c.periodYear) } : {}),
    value: c.mesureCount,
    status: c.status,
  }));

  const sample = filteredCells.find((c) => c.mesureCount > 0);
  const periodLabel =
    activeYear === ALL_YEARS
      ? years.length
        ? `${years[0]}–${years[years.length - 1]}`
        : null
      : activeYear;

  return (
    <NbsChartContainer
      title="Matrice de couverture"
      description="Disponibilité des mesures publiées par indicateur, territoire et année. Filtrez par année pour alléger la lecture."
      period={
        [
          periodLabel,
          completenessRatio != null ? `Complétude ${(completenessRatio * 100).toFixed(0)} %` : null,
        ]
          .filter(Boolean)
          .join(" · ") || null
      }
      source="Mesures et indicateurs publiés"
      isLoading={Boolean(isLoading) && cells.length === 0}
      empty={!isLoading && filteredCells.length === 0}
      emptyMessage={
        activeYear === ALL_YEARS
          ? "Aucune cellule de couverture pour ces filtres."
          : `Aucune cellule de couverture pour l’année ${activeYear}.`
      }
      actions={
        years.length > 0 ? (
          <label className="nbs-chart-mode">
            Année
            <select
              aria-label="Année de la matrice de couverture"
              value={activeYear}
              onChange={(e) => setUserYear(e.target.value)}
            >
              <option value={ALL_YEARS}>Toutes les années</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        ) : null
      }
      table={
        sample ? (
          <p className="nbs-chart-container__alt">
            <Link className="nbs-text-link" to={`/n2?indicateurId=${sample.indicateurId}`}>
              Ouvrir un indicateur N2
            </Link>
            {" · "}
            {onResetFilters ? (
              <button type="button" className="nbs-link-button" onClick={onResetFilters}>
                Réinitialiser les filtres
              </button>
            ) : null}
          </p>
        ) : (
          <Text tone="muted">Utilisez la matrice pour identifier les trous de données.</Text>
        )
      }
    >
      <div id="coverage">
        <NbsHeatmap cells={heatmapCells} />
      </div>
    </NbsChartContainer>
  );
}
